/**
 * How a set of people is named in a conversation title.
 *
 * Shared because the sidebar builds two kinds of row from two different sources —
 * flat DM rows are titled server-side by `buildDmTitle`, grouped People/Arc rows
 * are labelled client-side from `ListConversationGroups` — and a column where
 * half the rows say "Michael Abadie, Kaushal Mehta" and the other half say
 * "Michael, Kaushal" is worse than either rule applied consistently.
 */

/** Named in full before the remainder collapses to a count. */
const MAX_NAMED = 3

/** Kept at the front once the list is too long to spell out. */
const NAMES_WHEN_TRUNCATED = 2

/**
 * "Michael Abadie" -> "Michael". An email fallback (`displayName` uses one when a
 * user has no name) has no space and is returned whole rather than cut at the @.
 */
export const firstNameOf = (name: string): string => name.trim().split(/\s+/)[0] || name.trim()

/**
 * A participant set as a sidebar-width title, from names that are ALREADY short.
 *
 * Takes short names rather than applying `firstNameOf` itself: a nickname must
 * survive whole (see `shortDisplayName`), and only the server knows whether a
 * given name came from one. Callers pass `member.shortName`.
 *
 * - 1: `Ben`
 * - 2: `Ben and Mike`
 * - 3: `Ben, Mike, and Kaushal`
 * - 4+: `Ben, Mike, and 2 others`
 *
 * The requested rules read "3 or less → name everyone" and "3+ → collapse", which
 * overlap at exactly three. Three is named in full here, matching the worked
 * example that was given for it (`Ben, Mike, and Kaushal`).
 *
 * Callers that need the unabridged list — a tooltip, a search index — should keep
 * the names and use `formatParticipantTooltip`, not try to reverse this.
 */
export const formatParticipantTitle = (names: string[]): string => {
  const firstNames = names.map((name) => name.trim()).filter(Boolean)
  if (firstNames.length === 0) return ''
  if (firstNames.length === 1) return firstNames[0]
  if (firstNames.length === 2) return `${firstNames[0]} and ${firstNames[1]}`
  if (firstNames.length <= MAX_NAMED) {
    return `${firstNames.slice(0, -1).join(', ')}, and ${firstNames[firstNames.length - 1]}`
  }

  const shown = firstNames.slice(0, NAMES_WHEN_TRUNCATED)
  const remaining = firstNames.length - NAMES_WHEN_TRUNCATED
  return `${shown.join(', ')}, and ${remaining} ${remaining === 1 ? 'other' : 'others'}`
}

/**
 * The unabridged roster, for the hover that answers "who are the other 2?".
 *
 * Full names rather than first names: the tooltip exists precisely because the
 * title dropped information, so repeating the title's abbreviation in it would
 * make it pointless. Returns null when the title already names everyone, so a
 * caller can skip rendering a tooltip that would just restate the row.
 */
export const formatParticipantTooltip = (names: string[]): string | null => {
  const present = names.map((name) => name.trim()).filter(Boolean)
  if (present.length <= MAX_NAMED) return null
  return present.join(', ')
}
